import React, { useEffect, useRef, useState } from 'react';
import { useStickToBottom } from '../../../modules/vendor/assistant/use-stick-to-bottom';
import { ArrowLeft, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAskAi } from '../../../modules/vendor/assistant/AskAiContext';
import { conversationTimeline } from '../../../modules/vendor/assistant/conversation-timeline';
import { ConversationMenu } from '../../../modules/vendor/assistant/components/ConversationMenu';
import { MessageBody, ProposalCard, ProposalLike } from '../../../modules/vendor/assistant/components/ReplyParts';
import { AgentAvatar } from '../../../modules/vendor/agents/components/AgentAvatar';
import { Composer } from '../../../modules/vendor/agents/components/Composer';
import { LiveWork, TeamRecap } from '../../../modules/vendor/agents/components/LiveWork';
import { AssistantMessage } from '../../../common/hooks/use-assistant-api.hook';
import { SHELL_HEIGHT } from '../../../modules/vendor/assistant/askai-layout';
import { TopNavBar } from '../../../common/Header/TopNavBar';

/**
 * A conversation with the team, as a page.
 *
 * The side panel is for a quick question beside other work; this is where a
 * question that sends the team off to look at things is followed — full width,
 * so four specialists can work side by side and the vendor can watch them. It
 * is the same thread underneath (AskAiContext), so a conversation started here
 * is there in the panel and the other way round.
 *
 * The address carries the thread, so a conversation can be bookmarked, reopened
 * after a reload, or linked from Home's recents.
 */
const AgentChatPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { threadUuid } = useParams<{ threadUuid?: string }>();
    const askAi = useAskAi();
    const endRef = useRef<HTMLDivElement>(null);
    const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});

    // One conversation on screen at a time: the page replaces the panel.
    useEffect(() => {
        askAi?.close();
        void askAi?.refreshThreads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The address decides the thread. Opening one already on screen is a no-op.
    useEffect(() => {
        if (!askAi || !threadUuid || threadUuid === askAi.threadUuid) return;
        void askAi.openThread(threadUuid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadUuid]);

    // A conversation that just got its id takes it into the address.
    useEffect(() => {
        if (askAi?.threadUuid && askAi.threadUuid !== threadUuid) {
            navigate(`/home/chat/${askAi.threadUuid}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [askAi?.threadUuid]);

    // Follow the work as it arrives — including what renders after the reply
    // lands (charts, pictures, proposal cards). See use-stick-to-bottom.
    const scrollerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const asked = askAi?.messages.filter((m) => m.role === 'vendor').length ?? 0;
    useStickToBottom(scrollerRef, contentRef, `${askAi?.threadUuid ?? ''}:${asked}`);

    if (!askAi) return null;

    const { entries, pending } = conversationTimeline(askAi.messages, askAi.actions);
    const title = askAi.threads.find((th) => th.uuid === askAi.threadUuid)?.title;

    const reply = (m: AssistantMessage, key: string) =>
        m.role === 'vendor' ? (
            <div key={key} className="max-w-[80%] self-end rounded-2xl bg-muted px-4 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap">
                <MessageBody message={m} onLink={navigate} />
            </div>
        ) : (
            <div key={key} className="flex gap-3">
                <AgentAvatar agent="lead" size="sm" className="mt-0.5" />
                <div className="min-w-0 flex-1 text-sm leading-relaxed break-words whitespace-pre-wrap">
                    {!!m.team?.length && <TeamRecap team={m.team} />}
                    <MessageBody message={m} onLink={navigate} onAsk={(message) => void askAi.send(message)} busy={askAi.sending} />
                </div>
            </div>
        );

    const proposal = (a: ProposalLike) => (
        <div key={a.actionId} className="ps-9">
            <ProposalCard
                action={a}
                edits={edits[a.actionId] ?? {}}
                onEdit={(path, value) =>
                    setEdits((prev) => ({ ...prev, [a.actionId]: { ...(prev[a.actionId] ?? {}), [path]: value } }))
                }
                onApply={() => askAi.apply(a.actionId, edits[a.actionId] ?? {})}
                onDecline={() => askAi.decline(a.actionId)}
                handlers={{ onAsk: (message) => void askAi.send(message), onLink: navigate, busy: askAi.sending }}
            />
        </div>
    );

    const empty = !askAi.messages.length && !askAi.sending;

    return (
        // Exactly the shell's height, so the thread scrolls and the composer
        // stays pinned under it rather than riding down the page.
        <div className="flex min-h-0 w-full flex-col" style={{ height: SHELL_HEIGHT }}>
            <TopNavBar breadcrumbs={[{ label: t('AGENTS.NAV.CONVERSATIONS', 'Conversations') }]} />
            <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b px-4 md:px-6">
                <div className="flex min-w-0 items-center gap-2">
                    <Button variant="ghost" size="icon-sm" onClick={() => navigate('/home/dashboard')} aria-label={t('AGENTS.CHAT.BACK', 'Back to Home') as string}>
                        <ArrowLeft className="size-4 rtl:rotate-180" />
                    </Button>
                    <ConversationMenu
                        threads={askAi.threads}
                        activeUuid={askAi.threadUuid}
                        onOpen={(uuid) => navigate(`/home/chat/${uuid}`)}
                        onNew={() => { askAi.newChat(); navigate('/home/chat'); }}
                        onRemove={async (uuid) => {
                            await askAi.removeThread(uuid);
                            if (uuid === threadUuid) navigate('/home/chat', { replace: true });
                        }}
                    />
                    {title && <span className="sr-only">{title}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={() => { askAi.newChat(); navigate('/home/chat'); }}>
                    <Plus data-icon="inline-start" />
                    {t('AGENTS.CHAT.NEW', 'New conversation')}
                </Button>
            </div>

            <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto">
                <div ref={contentRef} className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pt-8 pb-12 md:px-6">
                    {empty && (
                        <div className="flex flex-col items-center gap-3 py-16 text-center">
                            <AgentAvatar agent="lead" size="lg" />
                            <p className="text-lg font-medium tracking-tight">{t('AGENTS.CHAT.EMPTY_TITLE', 'What should your team look into?')}</p>
                            <p className="max-w-md text-sm text-muted-foreground">
                                {t('AGENTS.CHAT.EMPTY_HINT', 'Ask a question and Ardealy sends the parts to the right specialists — you watch them work, then get one answer.')}
                            </p>
                        </div>
                    )}

                    {entries.map((entry) =>
                        entry.kind === 'message' ? reply(entry.message, entry.key) : proposal(entry.action),
                    )}

                    {askAi.sending && askAi.live && (
                        <div className="flex gap-3">
                            <AgentAvatar agent="lead" size="sm" working className="mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <LiveWork turn={askAi.live} />
                            </div>
                        </div>
                    )}

                    {pending.map((a) => proposal(a))}

                    {askAi.error && <p className={cn('ps-9 text-sm text-destructive')}>{askAi.error}</p>}
                    <div ref={endRef} />
                </div>
            </div>

            {/*
              * The composer floats: no rule across the page, a margin under it,
              * and the thread fading out as it scrolls beneath rather than
              * being cut by a line.
              */}
            <div className="relative shrink-0 px-4 pt-1 pb-5 md:px-6 md:pb-6">
                <div className="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-gradient-to-t from-background to-transparent" aria-hidden />
                <div className="mx-auto w-full max-w-3xl">
                    <Composer variant="dock" onSubmit={(text) => void askAi.send(text)} autoFocus />
                </div>
            </div>
        </div>
    );
};

export default AgentChatPage;
